import useConditionalEffect from 'hooks/useConditionalEffect'
import {
  ARTICLE_TYPE,
  AnyContent,
  Article,
  GIF_TYPE,
  Gif,
  PHOTO_TYPE,
  Photo,
  STATUS_TYPE,
  StatusIdea,
  Stream,
  VIDEO_TYPE,
  Video
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { Observable, animationFrameScheduler, empty as observableEmpty, of as observableOf } from 'rxjs'
import { mergeAll, mergeMap, observeOn, toArray } from 'rxjs/operators'
import { getArticles } from 'services/content/articles/actions'
import { getGifs } from 'services/content/gifs/actions'
import { getPhotos } from 'services/content/photos/actions'
import { getStatusIdeas } from 'services/content/statuses/actions'
import { getTexts } from 'services/content/texts/actions'
import { getVideos } from 'services/content/videos/actions'
import { StoreState, StoreThunkDispatch } from 'store/state'
import { createHandledPipe } from './FetchError'
import { ContentType } from 'shared'

export interface FetchStreamContentProps {
  stream: Stream
  hash?: string
  onFetched(content: AnyContent[]): void
  onFailed?(err: any): void
}

export interface ConnectedFetchStreamContentProps extends FetchStreamContentProps {
  hash: string
  only?: ContentType
  getVideos(): Observable<Video>
  getPhotos(): Observable<Photo>
  getArticles(): Observable<Article>
  getGifs(): Observable<Gif>
  getTexts(): Observable<any>
  getStatusIdeas(source: string[]): Observable<StatusIdea>
}

export const FetchStreamContent = ({
  stream,
  only,
  hash,
  onFetched,
  onFailed,
  getVideos,
  getPhotos,
  getArticles,
  getGifs,
  getTexts,
  getStatusIdeas
}: ConnectedFetchStreamContentProps) => {
  const subscriptionRef = useRef<any>(null)

  const handleFailed = useCallback((err: any) => {
    if (onFailed) {
      onFailed(err)
    }
  }, [onFailed])

  const fetch = useCallback(() => {
    const filters = stream.filters
    const types = filters && filters.length ? filters : [PHOTO_TYPE, ARTICLE_TYPE, VIDEO_TYPE]

    const skipVideos = only && only !== VIDEO_TYPE
    const skipPhotos = only && only !== PHOTO_TYPE
    const skipArticles = only && only !== ARTICLE_TYPE
    const skipGifs = only && only !== GIF_TYPE
    const skipStatuses = only && only !== STATUS_TYPE
    const skipTexts = only && only !== TEXT_TYPE

    const fetchPhotos = !skipPhotos && types.includes(PHOTO_TYPE)
    const fetchArticles = !skipArticles && types.includes(ARTICLE_TYPE)
    const fetchVideos = !skipVideos && types.includes(VIDEO_TYPE)
    const fetchGifs = !skipGifs && types.includes(GIF_TYPE)
    const fetchStatuses = !skipStatuses && types.includes(STATUS_TYPE)
    const fetchTexts = !skipTexts && types.includes(TEXT_TYPE)

    const requests = ([] as Observable<AnyContent>[])
      .concat(fetchPhotos ? [createHandledPipe(PHOTO_TYPE, getPhotos(), handleFailed)] : [])
      .concat(fetchArticles ? [createHandledPipe(ARTICLE_TYPE, getArticles(), handleFailed)] : [])
      .concat(fetchVideos ? [createHandledPipe(VIDEO_TYPE, getVideos(), handleFailed)] : [])
      .concat(fetchGifs ? [createHandledPipe(GIF_TYPE, getGifs(), handleFailed)] : [])
      .concat(fetchStatuses ? [createHandledPipe(STATUS_TYPE, getStatusIdeas([]), handleFailed)] : [])
      .concat(fetchTexts ? [createHandledPipe(TEXT_TYPE, getTexts(), handleFailed)] : [])

    return observableOf(...requests).pipe(
      mergeAll(),
      toArray(),
      observeOn(animationFrameScheduler))
      .subscribe({ next: onFetched, error: handleFailed })
  }, [stream, only, getPhotos, getArticles, getVideos, getGifs, getTexts, getStatusIdeas, onFetched, handleFailed])

  useConditionalEffect(([fetchContent]) => {
    subscriptionRef.current = fetchContent()

    return () => {
      subscriptionRef.current?.unsubscribe()
    }
  }, [hash], [fetch])

  return null
}

function mapStateToProps(_state: StoreState, ownProps: FetchStreamContentProps) {
  const hash = ownProps.hash || ''

  return {
    ...ownProps,
    hash
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch, ownProps: FetchStreamContentProps) {
  const { stream } = ownProps
  const id = stream.originalId || stream.id

  return {
    getVideos: () => dispatch(getVideos(id, true, undefined, undefined)).pipe(
      mergeMap(v => observableOf(...v.items))),
    getPhotos: () => dispatch(getPhotos(id, true, undefined, undefined)).pipe(
      mergeMap(p => observableOf(...p.items))),
    getArticles: () => dispatch(getArticles(id, true, undefined, undefined)).pipe(
      mergeMap(a => observableOf(...a.items))),
    getGifs: () => dispatch(getGifs(id, true, undefined, undefined)).pipe(
      mergeMap(a => observableOf(...a.items))),
    getTexts: () => dispatch(getTexts(id, true, undefined, undefined)).pipe(
      mergeMap(a => observableOf(...a.items))),
    getStatusIdeas: (categoryIds: Array<string>) => {
      if (categoryIds.length === 0) {
        return observableEmpty()
      }
      return dispatch(getStatusIdeas(categoryIds)).pipe(mergeMap(s => observableOf(...s.items)))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FetchStreamContent as any)
